import { defineStore, storeToRefs } from 'pinia';
import { api } from '@/services/http.service';
import { ApiErrorResponse } from '@/models/api/api-error.model';
import { ElNotification } from 'element-plus';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_LIMIT, DEFAULT_OFFSET } from '@/constants';
import { RealMoneyBalancePlayerListItemModel } from '@/models/mga-real-money-balance/mga-real-money-balance.model';
import { ResponseHeaders } from '@/models/app-internal/headers.model';
import { clearObj, getSortValue, SortingMapping, formatDatesInPayload } from '@marfa/bo-utils';
import {
  useMgaRealMoneyBalanceListFilterStore,
} from '@/store/mga-real-money-balance/mga-real-money-balance-list-filter.store';
import { GroupByDate } from '@/models/app-common/app-common.model';
import {
  MgaRealMoneyBalanceListFilterFields,
} from '@/models/mga-real-money-balance/mga-real-money-balance-list-filter.model';

type MgaReportRealMoneyBalanceListState = {
  realMoneyBalancePlayerList: RealMoneyBalancePlayerListItemModel[];
  isRealMoneyBalancePlayerListLoading: boolean;
  limit: number;
  offset: number;
  sort: string;
  listItemsCount: number;
  groupByDate: GroupByDate;
  currentPage: number;
  itemsPerPage: number;
}

export const useMgaReportRealMoneyBalanceListStore = defineStore({
  id: 'MgaReportRealMoneyBalanceListStore',
  state: (): MgaReportRealMoneyBalanceListState => ({
    realMoneyBalancePlayerList: [],
    isRealMoneyBalancePlayerListLoading: false,
    limit: DEFAULT_LIMIT,
    offset: DEFAULT_OFFSET,
    groupByDate: GroupByDate.MONTH,
    sort: '',
    listItemsCount: 0,
    currentPage: 1,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  }),
  actions: {
    setCurrentPage(page: number): void {
      this.currentPage = page;
    },
    setItemsPerPage(itemsPerPage: number): void {
      this.itemsPerPage = itemsPerPage;
    },
    setOffset(offset: number): void {
      this.offset = offset;
    },
    setLimit(limit: number): void {
      this.limit = limit;
    },
    setSort(sortOrder: string): void {
      this.sort = sortOrder;
    },
    setListItemsCount(count: number): void {
      this.listItemsCount = count;
    },
    processResponseHeaders(headers: Record<string, string>): void {
      const listItemsCount = Number(headers[ResponseHeaders.LIST_ITEMS_COUNT_HEADER]);

      if (isNaN(listItemsCount) || listItemsCount < 0) {
        return;
      }

      this.setListItemsCount(listItemsCount);
    },
    setRealMoneyBalancePlayerListLoading(isLoading: boolean): void {
      this.isRealMoneyBalancePlayerListLoading = isLoading;
    },
    setDefaultPaginationSettings(): void {
      this.setCurrentPage(1);
      this.setOffset(DEFAULT_OFFSET);
      this.setLimit(DEFAULT_LIMIT);
      this.setItemsPerPage(DEFAULT_ITEMS_PER_PAGE);
    },
    setDefaultSorting(): void {
      this.setSort('');
    },
    getRealMoneyBalancePlayerList(
    ): Promise<RealMoneyBalancePlayerListItemModel[] | void> {
      const { filterFormData } = storeToRefs(useMgaRealMoneyBalanceListFilterStore());

      this.setRealMoneyBalancePlayerListLoading(true);

      const requestPayload = {
        filterList: clearObj(formatDatesInPayload({
          payload: { ...filterFormData.value },
          isUnixToString: false,
          isIncludeTime: true,
        }, [
          MgaRealMoneyBalanceListFilterFields.DATE_FROM,
          MgaRealMoneyBalanceListFilterFields.DATE_TO,
          MgaRealMoneyBalanceListFilterFields.EXCHANGE_RATE_DATE,
        ])),
        offset: this.offset,
        limit: this.limit,
        sort: this.sort,
      };

      return api.mgaRealMoneyBalance.getRealMoneyBalancePlayerList.perform(requestPayload)
        .then(({ data, headers }) => {
          this.realMoneyBalancePlayerList = data;
          this.processResponseHeaders(headers);

          return data;
        }).catch((error) => {
          const apiError = error as ApiErrorResponse;
          ElNotification({
            title: 'Error',
            message: apiError.response.data.message,
            type: 'error',
          });
        }).finally(() => {
          this.setRealMoneyBalancePlayerListLoading(false);
        });
    },
    updateCurrentPage(page: number): void {
      this.setCurrentPage(page);
      this.setOffset((page - 1) * this.limit);
      this.getRealMoneyBalancePlayerList();
    },
    updatePaginationLimit(limit: number): void {
      this.setLimit(limit);
      this.setOffset(DEFAULT_OFFSET);
      this.setCurrentPage(1);
      this.getRealMoneyBalancePlayerList();
    },
    updateListSorting({ prop, order }): void {
      this.setSort(getSortValue({ columnName: prop, order: SortingMapping[order] }));
      this.getRealMoneyBalancePlayerList();
    },
  },
});
